import { Injectable, effect, inject } from "@angular/core";
import {
  BarcodeScanningService,
  BarcodeScanResult,
} from "./barcode-scanning.service";
import {
  Barcode,
  BarcodeAr,
  BarcodeArCircleHighlight,
  BarcodeArCircleHighlightPreset,
  BarcodeArHighlight,
  BarcodeArSettings,
  BarcodeArView,
  BarcodeArViewSettings,
  BarcodeArViewUiListener,
  barcodeCaptureLoader,
  Symbology,
} from "@scandit/web-datacapture-barcode";
import {
  Anchor,
  Brush,
  Camera,
  Color,
  DataCaptureContext,
  DataCaptureView,
  FrameSourceState,
  MarginsWithUnit,
  MeasureUnit,
  NumberWithUnit,
  PointWithUnit,
  ScanditIconBuilder,
  ScanditIconShape,
  ScanditIconType,
} from "@scandit/web-datacapture-core";
import { environment } from "../../../environments/environment";
import { ScannedProduct } from "./scanned-products.service";
import { normalizeBarcode, barcodesMatch } from "../utils/barcode.utils";
import { DeviceDetectionService } from "./device-detection.service";

// highlight.size is the only effective size control — ScanditIconBuilder's
// withWidth/withHeight are overridden internally by BarcodeArCircleHighlight.updateSize()
const HIGHLIGHT_CIRCLE_SIZE = 40;

@Injectable({
  providedIn: "root",
})
export class ScanditScannerService {
  private barcodeScanningService = inject(BarcodeScanningService);
  private deviceDetectionService = inject(DeviceDetectionService);

  // Track active highlights by barcode data
  private activeHighlights = new Map<string, BarcodeArCircleHighlight>();
  // Track previous product SKUs to detect removals
  private previousProductSkus = new Set<string>();
  // Track barcodes that resulted in errors (so they persist red icon)
  private errorBarcodes = new Set<string>();

  constructor() {
    // Watch for product removals and update highlights accordingly
    effect(() => {
      const scannedProducts = this.barcodeScanningService.scannedProducts();
      const currentProductSkus = new Set(Object.keys(scannedProducts));

      // Find SKUs that were removed (existed before but not now)
      const removedSkus = new Set<string>();
      for (const sku of this.previousProductSkus) {
        if (!currentProductSkus.has(sku)) {
          removedSkus.add(sku);
        }
      }

      // Only update highlights if products were actually removed
      if (removedSkus.size > 0) {
        // Find barcodes associated with removed products
        const removedBarcodes: string[] = [];

        for (const [barcodeData] of this.activeHighlights.entries()) {
          // Check if this highlight's barcode still has an associated product
          // Reuse findProductByBarcode for flexible matching
          const hasMatchingProduct =
            this.findProductByBarcode(barcodeData) !== undefined;

          if (!hasMatchingProduct) {
            removedBarcodes.push(barcodeData);
          }
        }

        // Update highlights for removed products
        for (const barcodeData of removedBarcodes) {
          this.updateHighlightToUnselected(barcodeData);
        }
      }

      // Update previous state for next comparison
      this.previousProductSkus = currentProductSkus;
    });
  }

  private async updateHighlightToUnselected(
    barcodeData: string,
  ): Promise<void> {
    const highlight = this.activeHighlights.get(barcodeData);
    if (!highlight) return;

    try {
      const toPickIcon = await new ScanditIconBuilder()
        .withBackgroundShape(ScanditIconShape.Circle)
        .withIcon(ScanditIconType.ToPick)
        .withIconColor(Color.fromRGBA(255, 255, 255, 1))
        .build();

      highlight.icon = toPickIcon;
      highlight.brush = new Brush(
        Color.fromRGBA(0, 191, 255, 1),
        Color.fromRGBA(0, 191, 255, 1),
        1,
      );
      highlight.isPulsing = true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // Highlight may have been disposed if barcode is no longer visible
      // Remove from tracking to prevent memory leaks
      this.activeHighlights.delete(barcodeData);
      console.debug(
        `Highlight for barcode ${barcodeData} was disposed, removing from tracking`,
      );
    }
  }

  public context: DataCaptureContext | undefined;
  public view: DataCaptureView | undefined;
  public camera: Camera | undefined;
  public settings: BarcodeArSettings | undefined;
  public barcodeAr: BarcodeAr | undefined;
  public barcodeArView: BarcodeArView | undefined;
  public host: HTMLElement | undefined;
  public barcodeArViewUiListener: BarcodeArViewUiListener | undefined;

  public async start(htmlElement: HTMLElement): Promise<void> {
    await this.initialize();
    document.getElementById("data-capture-view")!.style.visibility = "visible";
    await this.connectToView(htmlElement);
    await this.setScanArea();
    await this.setupBarcodeArView();
    await this.enableCamera(true);
    await this.enableScanning(true);
    this.addListener();
    await this.barcodeArView?.start();
  }

  public async resume(htmlElement: HTMLElement): Promise<void> {
    await this.connectToView(htmlElement);
    await this.setScanArea();
    await this.setupBarcodeArView();
    this.enableCamera(true);
    this.enableScanning(true);
    this.addListener();
    await this.barcodeArView?.start();
  }

  public pause(): void {
    this.barcodeArView?.stop();
    this.enableScanning(false);
    this.enableCamera(false);
    if (this.barcodeArViewUiListener && this.barcodeArView) {
      this.barcodeArView.listener = undefined;
    }
  }

  public async stop(): Promise<void> {
    this.cleanup();
  }

  async initialize() {
    const baseHref =
      document.querySelector("base")?.getAttribute("href") || "/";
    const normalizedBase = baseHref.endsWith("/") ? baseHref : `${baseHref}/`;
    const libraryLocation = `${window.location.origin}${normalizedBase}assets/scandit-lib/`;

    this.context = await DataCaptureContext.forLicenseKey(
      environment.scandit.license,
      {
        libraryLocation,
        moduleLoaders: [barcodeCaptureLoader()],
      },
    );
    this.view = await DataCaptureView.forContext(this.context);

    // Position the Scandit logo at the bottom center
    // Note: positive Y offset moves the logo down from the anchor point
    this.view.logoAnchor = Anchor.BottomCenter;
    this.view.logoOffset = new PointWithUnit(
      new NumberWithUnit(0, MeasureUnit.Fraction),
      new NumberWithUnit(0.35, MeasureUnit.Fraction),
    );

    this.settings = new BarcodeArSettings();
    this.settings.enableSymbologies([
      Symbology.EAN13UPCA,
      Symbology.EAN8,
      Symbology.UPCE,
      Symbology.Code39,
      Symbology.Code128,
    ]);

    this.barcodeAr = await BarcodeAr.forContext(this.context, this.settings);
  }

  async connectToView(htmlElement: HTMLElement) {
    this.host = htmlElement;
    this.view?.connectToElement(htmlElement);
  }

  async setScanArea() {
    if (this.view) {
      this.view.scanAreaMargins = new MarginsWithUnit(
        new NumberWithUnit(0.03, MeasureUnit.Fraction),
        new NumberWithUnit(0.015, MeasureUnit.Fraction),
        new NumberWithUnit(0.03, MeasureUnit.Fraction),
        new NumberWithUnit(0.2, MeasureUnit.Fraction),
      );
    }
  }

  async setupBarcodeArView() {
    if (!this.view || !this.context || !this.barcodeAr) {
      return;
    }

    // Create view settings with sound and haptic feedback enabled
    const viewSettings = new BarcodeArViewSettings();
    viewSettings.soundEnabled = true;
    viewSettings.hapticEnabled = true;

    // Create BarcodeArView with settings
    this.barcodeArView = await BarcodeArView.createWithSettings(
      this.view,
      this.context,
      this.barcodeAr,
      viewSettings,
    );

    // Enable zoom control button only on Zebra devices
    const isZebra = this.deviceDetectionService.isZebraDevice();
    if (isZebra) {
      this.barcodeArView.shouldShowZoomControl = isZebra;
      this.barcodeArView.zoomControlPosition = Anchor.TopRight;
    }

    this.barcodeArView.highlightProvider = {
      highlightForBarcode: async (
        barcode: Barcode,
        callback: (highlight: BarcodeArHighlight) => void,
      ): Promise<void> => {
        const highlight = BarcodeArCircleHighlight.create(
          barcode,
          BarcodeArCircleHighlightPreset.Icon,
        );
        highlight.size = HIGHLIGHT_CIRCLE_SIZE;
        const barcodeData = barcode.data || "";

        // Store highlight reference for later updates
        this.activeHighlights.set(barcodeData, highlight);

        const existingProduct = this.findProductByBarcode(barcodeData);
        const isErrorBarcode = this.errorBarcodes.has(barcodeData);

        if (existingProduct) {
          // Success - show green checkmark
          const checkMarkIcon = await new ScanditIconBuilder()
            .withBackgroundShape(ScanditIconShape.Circle)
            .withIcon(ScanditIconType.Checkmark)
            .withIconColor(Color.fromRGBA(255, 255, 255, 1))
            .build();

          highlight.icon = checkMarkIcon;
          highlight.brush = new Brush(
            Color.fromRGBA(13, 133, 61, 1),
            Color.fromRGBA(13, 133, 61, 1),
            1,
          );
          highlight.isPulsing = false;
        } else if (isErrorBarcode) {
          // Error - show red square with wrong item icon (persisted)
          const errorIcon = await new ScanditIconBuilder()
            .withBackgroundShape(ScanditIconShape.Square)
            .withIcon(ScanditIconType.WrongItem)
            .withIconColor(Color.fromRGBA(255, 255, 255, 1))
            .build();

          highlight.icon = errorIcon;
          highlight.brush = new Brush(
            Color.fromRGBA(220, 53, 69, 1),
            Color.fromRGBA(220, 53, 69, 1),
            2,
          );
          highlight.isPulsing = false;
        } else {
          // Default - show blue "to pick" icon
          const toPickIcon = await new ScanditIconBuilder()
            .withBackgroundShape(ScanditIconShape.Circle)
            .withIcon(ScanditIconType.ToPick)
            .withIconColor(Color.fromRGBA(255, 255, 255, 1))
            .build();

          highlight.icon = toPickIcon;
          highlight.brush = new Brush(
            Color.fromRGBA(0, 191, 255, 1),
            Color.fromRGBA(0, 191, 255, 1),
            1,
          );
          highlight.isPulsing = true;
        }

        callback(highlight);
      },
    };
    this.barcodeArView.annotationProvider = undefined;
  }

  async enableCamera(enabled: boolean) {
    if (this.camera) {
      await this.camera.switchToDesiredState(
        enabled ? FrameSourceState.On : FrameSourceState.Standby,
      );
    }
  }

  async enableScanning(enabled: boolean) {
    await this.barcodeAr?.setEnabled(enabled);
  }

  async addListener() {
    // Set up UI listener for highlight taps (if needed)
    if (this.barcodeArView) {
      this.barcodeArViewUiListener = {
        didTapHighlightForBarcode: async (
          barcodeAr: BarcodeAr,
          barcode: Barcode,
          highlight: BarcodeArCircleHighlight,
        ) => {
          const barcodeData = barcode.data || "";
          const productToRemove = this.findProductByBarcode(barcodeData);

          if (productToRemove) {
            // Remove the product and revert to blue pulsing state
            this.barcodeScanningService.removeProduct(
              productToRemove.product.upc.toString(),
            );

            const toPickIcon = await new ScanditIconBuilder()
              .withBackgroundShape(ScanditIconShape.Circle)
              .withIcon(ScanditIconType.ToPick)
              .withIconColor(Color.fromRGBA(255, 255, 255, 1))
              .build();

            highlight.icon = toPickIcon;
            highlight.brush = new Brush(
              Color.fromRGBA(0, 191, 255, 1),
              Color.fromRGBA(0, 191, 255, 1),
              1,
            );
            highlight.isPulsing = true;
          } else {
            // Process barcode and show appropriate icon based on result
            this.barcodeScanningService.processBarcode(barcodeData).subscribe({
              next: async (result) => {
                if (result.success) {
                  // Success - remove from error tracking if it was there (retry succeeded)
                  this.errorBarcodes.delete(barcodeData);

                  // Success - show green checkmark
                  const checkMarkIcon = await new ScanditIconBuilder()
                    .withBackgroundShape(ScanditIconShape.Circle)
                    .withIcon(ScanditIconType.Checkmark)
                    .withIconColor(Color.fromRGBA(255, 255, 255, 1))
                    .build();

                  highlight.icon = checkMarkIcon;
                  highlight.brush = new Brush(
                    Color.fromRGBA(13, 133, 61, 1),
                    Color.fromRGBA(13, 133, 61, 1),
                    2,
                  );
                  highlight.isPulsing = false;
                } else {
                  // Failed - show red square with wrong item icon
                  // Track this barcode as an error so it persists
                  this.errorBarcodes.add(barcodeData);

                  const errorIcon = await new ScanditIconBuilder()
                    .withBackgroundShape(ScanditIconShape.Square)
                    .withIcon(ScanditIconType.WrongItem)
                    .withIconColor(Color.fromRGBA(255, 255, 255, 1))
                    .build();

                  highlight.icon = errorIcon;
                  highlight.brush = new Brush(
                    Color.fromRGBA(220, 53, 69, 1),
                    Color.fromRGBA(220, 53, 69, 1),
                    2,
                  );
                  highlight.isPulsing = false;
                }
              },
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              error: async (errorResult: BarcodeScanResult) => {
                // Track this barcode as an error so it persists
                this.errorBarcodes.add(barcodeData);

                const errorIcon = await new ScanditIconBuilder()
                  .withBackgroundShape(ScanditIconShape.Square)
                  .withIcon(ScanditIconType.WrongItem)
                  .withIconColor(Color.fromRGBA(255, 255, 255, 1))
                  .build();

                highlight.icon = errorIcon;
                highlight.brush = new Brush(
                  Color.fromRGBA(220, 53, 69, 1),
                  Color.fromRGBA(220, 53, 69, 1),
                  2,
                );
                highlight.isPulsing = false;
              },
            });
          }
        },
      };
      this.barcodeArView.listener = this.barcodeArViewUiListener;
    }
  }

  /**
   * Helper method to find a product by barcode data (supports both SKU and UPC lookup)
   * Handles EAN-13 vs UPC-A format differences (leading zeros)
   * @param barcodeData - The barcode data (could be SKU or UPC)
   * @returns The found product or undefined
   */
  private findProductByBarcode(
    barcodeData: string,
  ): ScannedProduct | undefined {
    const normalized = normalizeBarcode(barcodeData);

    // First try direct SKU lookup with normalized barcode
    let product = this.barcodeScanningService.getProductBySku(normalized);

    // Also try with original barcode data
    if (!product) {
      product = this.barcodeScanningService.getProductBySku(barcodeData);
    }

    // If not found, search by barcode/UPC in scanned products with flexible matching
    if (!product) {
      const scannedProducts = this.barcodeScanningService.scannedProducts();
      product = Object.values(scannedProducts).find(
        (p) =>
          barcodesMatch(barcodeData, p.barcode) ||
          barcodesMatch(barcodeData, p.product.upc?.toString()),
      );
    }

    return product;
  }

  async cleanup() {
    await this.barcodeArView?.stop();

    if (this.barcodeArViewUiListener && this.barcodeArView) {
      this.barcodeArView.listener = undefined;
      this.barcodeArViewUiListener = undefined;
    }

    if (this.barcodeArView) {
      this.barcodeArView.annotationProvider = undefined;
    }

    // Clear highlight and error tracking
    this.activeHighlights.clear();
    this.errorBarcodes.clear();

    await this.camera?.switchToDesiredState(FrameSourceState.Off);
    await this.context?.dispose();
    await this.context?.removeAllModes();
    this.view?.detachFromElement();
  }
}
